import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  Text,
  Icon,
  Link,
  Button,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FaGitAlt, FaCodeBranch, FaClock, FaUser, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useMvpMode } from '../config/mvp.jsx';

const DevFooter = () => {
  const { isMvpMode, toggleMvp } = useMvpMode();
  const [gitInfo, setGitInfo] = useState(null);

  useEffect(() => {
    // Intentar cargar la info de git
    fetch('/git-info.json')
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => setGitInfo(data))
      .catch(() => {
        // Si no existe, mostrar info por defecto
        setGitInfo({ error: true });
      });
  }, []);

  // Solo mostrar en desarrollo
  const isDev = import.meta.env.DEV || 
    window.location.hostname.includes('dev.') ||
    import.meta.env.VITE_ENV === 'development';

  if (!isDev) return null;
  if (!gitInfo) return null;

  if (gitInfo.error) {
    return (
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="orange.600"
        color="white"
        py={1}
        px={4}
        zIndex={9999}
        fontSize="xs"
      >
        <HStack justify="center" gap={2}>
          <Icon as={FaGitAlt} />
          <Text>Entorno de desarrollo - Sin info de git disponible</Text>
        </HStack>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      display={{ base: 'none', lg: 'block' }}
      bg="purple.700"
      color="white"
      py={1.5}
      px={4}
      zIndex={9999}
      fontSize="xs"
      boxShadow="0 -2px 10px rgba(0,0,0,0.3)"
    >
      <HStack justify="center" gap={6} flexWrap="wrap">
        {/* Entorno */}
        <HStack gap={1}>
          <Text fontWeight="bold" color="yellow.300">DEV</Text>
        </HStack>

        {/* MVP Toggle */}
        <Tooltip content={isMvpMode ? 'MVP activo: secciones ocultas. Click para desactivar' : 'MVP inactivo: todo visible. Click para activar'} showArrow>
          <Button
            size="xs"
            variant="outline"
            color={isMvpMode ? 'orange.300' : 'green.300'}
            borderColor={isMvpMode ? 'orange.300' : 'green.300'}
            _hover={{ bg: isMvpMode ? 'orange.600' : 'green.600' }}
            onClick={toggleMvp}
            h={6}
            fontSize="xs"><Icon as={isMvpMode ? FaToggleOn : FaToggleOff} />
            MVP {isMvpMode ? 'ON' : 'OFF'}</Button>
        </Tooltip>

        {/* Rama */}
        <Tooltip content="Rama" showArrow>
          <HStack gap={1}>
            <Icon as={FaCodeBranch} color="green.300" />
            <Text>{gitInfo.branch || 'N/A'}</Text>
          </HStack>
        </Tooltip>

        {/* Commit */}
        <Tooltip content={`Commit completo: ${gitInfo.commitFull || 'N/A'}`} showArrow>
          <HStack gap={1}>
            <Icon as={FaGitAlt} color="orange.300" />
            <Link
              href={gitInfo.commitUrl}
              _hover={{ textDecoration: 'underline' }}
              target='_blank'
              rel='noopener noreferrer'>
              {gitInfo.commitShort || 'N/A'}
            </Link>
          </HStack>
        </Tooltip>

        {/* Mensaje del commit */}
        <Tooltip content="Mensaje del commit" showArrow>
          <Text 
            maxW="300px" 
            isTruncated 
            color="gray.200"
            fontStyle="italic"
          >
            "{gitInfo.commitMessage || 'N/A'}"
          </Text>
        </Tooltip>

        {/* Autor */}
        <Tooltip content="Autor del commit" showArrow>
          <HStack gap={1}>
            <Icon as={FaUser} color="blue.300" />
            <Text>{gitInfo.author || 'N/A'}</Text>
          </HStack>
        </Tooltip>

        {/* Fecha */}
        <Tooltip content="Fecha del último deploy" showArrow>
          <HStack gap={1}>
            <Icon as={FaClock} color="cyan.300" />
            <Text>{formatDate(gitInfo.deployedAt)}</Text>
          </HStack>
        </Tooltip>
      </HStack>
    </Box>
  );
};

export default DevFooter;
